import React, { useEffect, useRef, useState } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { XMarkIcon, ArrowTopRightOnSquareIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import {
	createTelegramLinkToken,
	getTelegramLinkStatus,
	TelegramLinkTokenResponse,
} from '../../services/accountTelegramService';

interface Props {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	/** Колбек при успешной привязке (для refresh родительской карточки). */
	onLinked: () => void;
}

/**
 * Модалка привязки Telegram-бота (TASKNEIROKLYUCH-647 Bot API).
 *
 * Flow:
 *   1. При открытии запрашивает deep link (POST /account/telegram/link-token).
 *   2. Пользователь кликает «Открыть Telegram» — переходит в бота, жмёт Start.
 *   3. Параллельно идёт polling GET /account/telegram/status каждые 2 сек.
 *   4. Когда linked=true — toast success, onLinked(), закрытие.
 *   5. Если TTL 10 мин истёк — показываем «Получить новую ссылку».
 */
export const TelegramLinkModal: React.FC<Props> = ({ open, onOpenChange, onLinked }) => {
	const [token, setToken] = useState<TelegramLinkTokenResponse | null>(null);
	const [loading, setLoading] = useState(false);
	const [expired, setExpired] = useState(false);
	const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);

	// Генерируем токен при открытии модалки
	useEffect(() => {
		if (!open) {
			stopPolling();
			setToken(null);
			setExpired(false);
			return;
		}
		requestNewToken();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [open]);

	// Запускаем polling когда есть токен
	useEffect(() => {
		if (!open || !token || expired) return;
		pollRef.current = setInterval(async () => {
			try {
				const status = await getTelegramLinkStatus();
				if (status.linked) {
					stopPolling();
					toast.success('Telegram подключён — нотификации будут приходить в бота');
					onLinked();
					onOpenChange(false);
				}
			} catch {
				// сетевой сбой — следующий тик попробует ещё раз
			}
		}, 2000);

		// Таймер TTL: 10 минут
		const ttlMs = Math.max(0, new Date(token.expiresAt).getTime() - Date.now());
		const ttlTimer = setTimeout(() => {
			stopPolling();
			setExpired(true);
		}, ttlMs);

		return () => {
			stopPolling();
			clearTimeout(ttlTimer);
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [token, expired, open]);

	const stopPolling = () => {
		if (pollRef.current) {
			clearInterval(pollRef.current);
			pollRef.current = null;
		}
	};

	const requestNewToken = async () => {
		setLoading(true);
		setExpired(false);
		try {
			const result = await createTelegramLinkToken();
			setToken(result);
		} catch (e) {
			toast.error('Не удалось создать ссылку. Попробуйте ещё раз.');
		} finally {
			setLoading(false);
		}
	};

	const openTelegram = () => {
		if (token?.deepLink) {
			window.open(token.deepLink, '_blank', 'noopener,noreferrer');
		}
	};

	return (
		<Dialog.Root open={open} onOpenChange={onOpenChange}>
			<Dialog.Portal>
				<Dialog.Overlay className="fixed inset-0 bg-black bg-opacity-50 z-40" />
				<Dialog.Content className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-xl p-6 w-full max-w-md z-50">
					<div className="flex justify-between items-start mb-4">
						<Dialog.Title className="text-lg font-semibold text-gray-900">
							Подключение Telegram
						</Dialog.Title>
						<Dialog.Close className="text-gray-400 hover:text-gray-600" aria-label="Закрыть">
							<XMarkIcon className="w-5 h-5" />
						</Dialog.Close>
					</div>

					<Dialog.Description className="text-sm text-gray-600 mb-4">
						Перейдите в бота и нажмите <strong>Start</strong>. После этого нотификации рекрутера начнут приходить сюда.
					</Dialog.Description>

					{loading && (
						<div className="flex items-center justify-center py-6">
							<div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-900" />
							<span className="ml-3 text-sm text-gray-600">Генерируем ссылку...</span>
						</div>
					)}

					{!loading && token && !expired && (
						<>
							<button
								onClick={openTelegram}
								className="w-full flex items-center justify-center gap-2 bg-[#0088cc] text-white py-3 px-4 rounded-md hover:bg-[#0077b3] transition mb-3"
							>
								<ArrowTopRightOnSquareIcon className="w-5 h-5" />
								Открыть Telegram
							</button>
							<div className="flex items-center justify-center gap-2 text-xs text-gray-500">
								<div className="animate-pulse rounded-full w-2 h-2 bg-blue-500" />
								<span>Ожидаем подтверждения в боте...</span>
							</div>
							<p className="text-xs text-gray-400 mt-3 text-center">
								Ссылка действует 10 минут.
							</p>
						</>
					)}

					{!loading && expired && (
						<div className="text-center py-4">
							<p className="text-sm text-gray-700 mb-4">
								Время действия ссылки истекло.
							</p>
							<button
								onClick={requestNewToken}
								className="bg-gray-900 text-white py-2 px-4 rounded-md hover:bg-gray-800 transition"
							>
								Получить новую ссылку
							</button>
						</div>
					)}
				</Dialog.Content>
			</Dialog.Portal>
		</Dialog.Root>
	);
};
